import { useCallback, useEffect, useRef, useState } from 'react';

/* ------------------------------------------------------------------ */
/*  Fullscreen image lightbox with pinch-to-zoom & swipe-down-to-close */
/* ------------------------------------------------------------------ */

interface ImageLightboxProps {
  src: string;
  alt?: string;
  onClose: () => void;
}

/**
 * Renders a fullscreen overlay showing `src` image.
 * - Pinch-to-zoom (touch) and scroll-wheel zoom (desktop).
 * - Swipe / drag down to dismiss (fires `onClose`).
 * - Tap / click the dark backdrop to dismiss.
 * - Press Escape to dismiss.
 */
export function ImageLightbox({ src, alt = '', onClose }: ImageLightboxProps) {
  /* ---- state ---- */
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [closing, setClosing] = useState(false);

  /* refs for gesture tracking */
  const containerRef = useRef<HTMLDivElement>(null);
  const lastPinchDist = useRef<number | null>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const dragTranslate = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);

  /* ---- close with animation ---- */
  const animateClose = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 250);
  }, [onClose]);

  /* ---- keyboard: Escape ---- */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') animateClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [animateClose]);

  /* ---- lock body scroll while open ---- */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  /* ---- wheel zoom (desktop) ---- */
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    setScale((s) => Math.min(5, Math.max(0.5, s - e.deltaY * 0.002)));
  }, []);

  /* ---- touch gestures ---- */
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        // start pinch
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastPinchDist.current = Math.hypot(dx, dy);
      } else if (e.touches.length === 1 && scale <= 1) {
        // start drag (for swipe-down dismiss)
        dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        dragTranslate.current = { ...translate };
        isDragging.current = false;
      }
    },
    [scale, translate],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && lastPinchDist.current !== null) {
        // pinch zoom
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        const ratio = dist / lastPinchDist.current;
        lastPinchDist.current = dist;
        setScale((s) => Math.min(5, Math.max(0.5, s * ratio)));
        e.preventDefault();
      } else if (e.touches.length === 1 && dragStart.current && scale <= 1) {
        // drag / swipe
        const dy = e.touches[0].clientY - dragStart.current.y;
        const dx = e.touches[0].clientX - dragStart.current.x;
        isDragging.current = true;
        setTranslate({
          x: dragTranslate.current.x + dx,
          y: dragTranslate.current.y + dy,
        });
      }
    },
    [scale],
  );

  const handleTouchEnd = useCallback(
    (_e: React.TouchEvent) => {
      lastPinchDist.current = null;

      // swipe-down dismiss: if dragged down more than 120px
      if (isDragging.current && translate.y > 120) {
        animateClose();
      } else {
        // snap back
        setTranslate({ x: 0, y: 0 });
      }
      dragStart.current = null;
      isDragging.current = false;
    },
    [translate.y, animateClose],
  );

  /* ---- mouse drag (desktop fallback for swipe-down) ---- */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (scale > 1) return; // don't allow drag dismiss while zoomed
      e.preventDefault();
      dragStart.current = { x: e.clientX, y: e.clientY };
      dragTranslate.current = { ...translate };
      isDragging.current = false;

      const handleMouseMove = (me: MouseEvent) => {
        if (!dragStart.current) return;
        isDragging.current = true;
        const dy = me.clientY - dragStart.current.y;
        const dx = me.clientX - dragStart.current.x;
        setTranslate({
          x: dragTranslate.current.x + dx,
          y: dragTranslate.current.y + dy,
        });
      };

      const handleMouseUp = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        if (isDragging.current && translate.y > 120) {
          animateClose();
        } else {
          setTranslate({ x: 0, y: 0 });
        }
        dragStart.current = null;
        isDragging.current = false;
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [scale, translate, animateClose],
  );

  /* ---- backdrop click ---- */
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === containerRef.current) animateClose();
    },
    [animateClose],
  );

  /* ---- double-tap / double-click to reset zoom ---- */
  const handleDoubleClick = useCallback(() => {
    setScale((s) => (s > 1 ? 1 : 2.5));
    setTranslate({ x: 0, y: 0 });
  }, []);

  /* ---- opacity driven by drag-down distance ---- */
  const backdropOpacity = closing
    ? 0
    : Math.max(0.3, 1 - Math.abs(translate.y) / 400);

  return (
    <div
      ref={containerRef}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `rgba(0,0,0,${backdropOpacity * 0.92})`,
        transition: closing ? 'opacity 0.25s ease' : undefined,
        opacity: closing ? 0 : 1,
        cursor: 'zoom-out',
        touchAction: 'none',
      }}
    >
      {/* Close hint */}
      <div
        style={{
          position: 'absolute',
          top: 16,
          right: 20,
          color: 'rgba(255,255,255,0.6)',
          fontSize: 14,
          fontFamily: 'sans-serif',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        swipe down or tap backdrop to close
      </div>

      {/* Close button */}
      <button
        type="button"
        onClick={animateClose}
        style={{
          position: 'absolute',
          top: 12,
          left: 20,
          background: 'rgba(0,0,0,0.5)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '50%',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 22,
          cursor: 'pointer',
          zIndex: 2,
        }}
        aria-label="Close"
      >
        ✕
      </button>

      {/* Image */}
      <img
        src={src}
        alt={alt}
        draggable={false}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        style={{
          maxWidth: '95vw',
          maxHeight: '95vh',
          objectFit: 'contain',
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transition: isDragging.current ? 'none' : 'transform 0.2s ease',
          cursor: scale > 1 ? 'grab' : 'zoom-in',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      />
    </div>
  );
}
